import React, { Component } from "react";
import { connect } from "react-redux";
import { Button, Upload, Alert, Icon } from "antd";
import {
  BULK_ORDER_MAX_ROWS,
  BULK_ORDER_SAMPLE_PATH,
  BULK_ORDER_SAMPLE_DOWNLOAD_NAME,
} from "./constants";
import { validateBulkOrderCsvFile } from "./csvValidation";
import { createBulkOrder } from "./bulkOrderActions";
import { formatValidationErrorLine } from "./responseHandler";
import { errorToast } from "../../app/common/util/genericUtils";

class BulkOrderForm extends Component {
  state = {
    fileList: [],
    selectedFile: null,
    validationMessage: "",
    apiMessage: "",
    fileError: "",
    validationErrors: [],
    rowCount: 0,
    submitting: false,
  };

  clearApiErrors = () => ({
    apiMessage: "",
    fileError: "",
    validationErrors: [],
  });

  handleBeforeUpload = async (file) => {
    const result = await validateBulkOrderCsvFile(file);

    if (!result.valid) {
      errorToast("Invalid CSV", result.error);
      this.setState({
        fileList: [],
        selectedFile: null,
        validationMessage: result.error,
        rowCount: 0,
        ...this.clearApiErrors(),
      });
      return false;
    }

    this.setState({
      fileList: [
        {
          uid: file.uid || String(Date.now()),
          name: file.name,
          status: "done",
          originFileObj: result.cleanedFile || file,
        },
      ],
      selectedFile: result.cleanedFile || file,
      validationMessage: "",
      rowCount: result.rowCount,
      ...this.clearApiErrors(),
    });

    return false;
  };

  handleRemove = () => {
    this.setState({
      fileList: [],
      selectedFile: null,
      validationMessage: "",
      rowCount: 0,
      ...this.clearApiErrors(),
    });
  };

  handleSubmit = async () => {
    const { selectedFile } = this.state;
    const { createBulkOrder } = this.props;

    if (!selectedFile) {
      errorToast("Error", "Please select a CSV file to upload.");
      this.setState({
        apiMessage: "Please upload a CSV file.",
        fileError: "CSV file is required",
        validationErrors: [],
      });
      return;
    }

    const result = await validateBulkOrderCsvFile(selectedFile);
    if (!result.valid) {
      errorToast("Invalid CSV", result.error);
      this.setState({
        validationMessage: result.error,
        ...this.clearApiErrors(),
      });
      return;
    }

    this.setState({ submitting: true, ...this.clearApiErrors() });
    const response = await createBulkOrder(result.cleanedFile || selectedFile);

    if (!response || response.success) {
      this.setState({ submitting: false });
      return;
    }

    this.setState({
      submitting: false,
      apiMessage: response.uiMessage || "Please fix the CSV errors and try again.",
      fileError: response.fileError || "",
      validationErrors: response.validationErrors || [],
    });
  };

  renderApiErrors = () => {
    const { apiMessage, fileError, validationErrors } = this.state;
    if (!apiMessage && !fileError && validationErrors.length === 0) {
      return null;
    }

    return (
      <Alert
        className="bulk-order-alert"
        type="error"
        showIcon
        message={apiMessage || "Upload failed"}
        description={
          <div className="bulk-order-api-errors">
            {fileError ? (
              <p className="bulk-order-file-error">{fileError}</p>
            ) : null}
            {validationErrors.length > 0 ? (
              <ul className="bulk-order-error-list">
                {validationErrors.map((item, index) => (
                  <li key={`${item.row}-${item.field}-${index}`}>
                    {formatValidationErrorLine(item)}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        }
      />
    );
  };

  render() {
    const {
      fileList,
      selectedFile,
      validationMessage,
      rowCount,
      submitting,
      apiMessage,
      fileError,
      validationErrors,
    } = this.state;

    const hasApiErrors =
      Boolean(apiMessage || fileError) || validationErrors.length > 0;

    return (
      <div className="bulk-order-form">
        <p className="bulk-order-form-help">
          Download the sample CSV, fill order rows, then upload. Max{" "}
          {BULK_ORDER_MAX_ROWS} rows and 5 MB. CSV files only.{" "}
        </p>

        <div className="bulk-order-form-actions">
          <Button
            icon="download"
            href={BULK_ORDER_SAMPLE_PATH}
            download={BULK_ORDER_SAMPLE_DOWNLOAD_NAME}
          >
            Download Sample CSV
          </Button>
        </div>

        <Upload.Dragger
          className="bulk-order-upload"
          accept=".csv,text/csv"
          multiple={false}
          fileList={fileList}
          beforeUpload={this.handleBeforeUpload}
          onRemove={this.handleRemove}
          disabled={submitting}
        >
          <p className="ant-upload-drag-icon">
            <Icon type="inbox" />
          </p>
          <p className="ant-upload-text">Click or drag CSV file to upload</p>
          <p className="ant-upload-hint">Only .csv files up to 5 MB</p>
        </Upload.Dragger>

        {validationMessage ? (
          <Alert
            className="bulk-order-alert"
            type="error"
            showIcon
            message={validationMessage}
          />
        ) : null}

        {selectedFile && !validationMessage && !hasApiErrors ? (
          <Alert
            className="bulk-order-alert"
            type="success"
            showIcon
            message={`${selectedFile.name} ready (${rowCount} row${
              rowCount === 1 ? "" : "s"
            })`}
          />
        ) : null}

        {this.renderApiErrors()}

        <Button
          className="bulk-order-submit"
          type="primary"
          block
          loading={submitting}
          disabled={!selectedFile || submitting}
          onClick={this.handleSubmit}
        >
          Upload & Create Bulk Orders
        </Button>
      </div>
    );
  }
}

const actions = {
  createBulkOrder,
};

export default connect(null, actions)(BulkOrderForm);
